import React, { useEffect, useMemo, useState } from "react";
import { Accordion, AccordionDetails, AccordionSummary, Box, Grid, MenuItem, TextField, Typography, useMediaQuery } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import { alpha, useTheme } from "@mui/material/styles";

import {
  BasicDetailsStep,
  PermissionToggleCard,
  StickyModalFooter,
  WizardModalLayout,
} from "../modals/wizard/WizardComponents";
import { useCreateUserMutation, useEditUserMutation } from "../../redux/admin/adminApi";
import { ROLES, getPermissions, hasUserPermission } from "../../utils/rbacHelper";
import { normalizeUserId } from "../modals/permissionsDefaults";

const ROLE_LABELS = {
  [ROLES.MANAGER]: "Manager",
  [ROLES.HR]: "HR",
  [ROLES.OFFICE_EMPLOYEE]: "Office Employee",
  [ROLES.FIELD_EMPLOYEE]: "Field Employee",
};

const ORG_CREATABLE_BY_ROLE = {
  [ROLES.SUPER_ADMIN]: [
    ROLES.MANAGER,
    ROLES.HR,
    ROLES.OFFICE_EMPLOYEE,
    ROLES.FIELD_EMPLOYEE,
  ],
  [ROLES.MANAGER]: [ROLES.OFFICE_EMPLOYEE, ROLES.FIELD_EMPLOYEE],
  [ROLES.HR]: [ROLES.OFFICE_EMPLOYEE, ROLES.FIELD_EMPLOYEE],
};

const PERMISSION_GROUPS = [
  {
    title: "View Permissions",
    items: [
      { key: "viewDashboard", label: "View Dashboard" },
      { key: "viewUsers", label: "View Users" },
      { key: "viewDevices", label: "View Devices" },
      { key: "viewAssets", label: "View Assets" },
      { key: "viewEvents", label: "View Events" },
      { key: "viewMap", label: "View Live Map" },
      { key: "viewProtocols", label: "View Protocols" },
      { key: "manageSensors", label: "Manage Sensors" },
    ],
  },
  {
    title: "User Management",
    items: [
      { key: "createUser", label: "Create Users" },
      { key: "editUser", label: "Edit Users" },
      { key: "deleteUser", label: "Delete Users" },
      { key: "restoreUser", label: "Restore Users" },
      { key: "resetPassword", label: "Reset Password" },
      { key: "assignUserToGA", label: "Assign User To Dealer" },
      { key: "removeManagedUser", label: "Remove Managed User" },
      { key: "loginAsUser", label: "Login As User" },
    ],
  },
  {
    title: "Device Management",
    items: [
      { key: "createDevice", label: "Add Device" },
      { key: "editDevice", label: "Edit Device" },
      { key: "deleteDevice", label: "Delete Device" },
      { key: "assignDevice", label: "Assign Device" },
      { key: "sendCommands", label: "Send Commands" },
      { key: "manageGeofences", label: "Manage Geofences" },
    ],
  },
  {
    title: "Assets And Credits",
    items: [
      { key: "createAsset", label: "Add Assets" },
      { key: "editAsset", label: "Edit Assets" },
      { key: "deleteAsset", label: "Delete Assets" },
      { key: "restoreAsset", label: "Restore Assets" },
      { key: "bulkUpload", label: "Bulk Upload" },
      { key: "viewCredits", label: "View Credits" },
      { key: "allocateCredits", label: "Allocate Credits" },
      { key: "transferCredits", label: "Transfer Credits" },
      { key: "viewCreditHistory", label: "View Credit History" },
      { key: "manageCredits", label: "Manage Credits" },
      { key: "requestCredits", label: "Request Credits" },
    ],
  },
  {
    title: "Tasks And Other",
    items: [
      { key: "createTask", label: "Create Task" },
      { key: "assignTask", label: "Assign Task" },
      { key: "viewAllTasks", label: "View All Tasks" },
      { key: "viewMyTasks", label: "View My Tasks" },
      { key: "updateTask", label: "Update Task" },
      { key: "deleteTask", label: "Delete Task" },
      { key: "messageManagement", label: "Messages" },
    ],
  },
];

const buildRolePermissionState = (role) => {
  const defaults = getPermissions(role);
  const selected = {};
  PERMISSION_GROUPS.forEach((group) => {
    group.items.forEach((item) => {
      selected[item.key] = defaults[item.key] === true;
    });
  });
  return selected;
};

const toLegacyCrudPermissions = (appPermissions) => ({
  devicePermissions: {
    read: !!appPermissions.viewDevices,
    create: !!appPermissions.createDevice,
    update: !!appPermissions.editDevice,
    delete: !!appPermissions.deleteDevice,
    command: !!appPermissions.sendCommands,
  },
  userPermissions: {
    read: !!appPermissions.viewUsers,
    create: !!appPermissions.createUser,
    update: !!appPermissions.editUser,
    delete: !!appPermissions.deleteUser,
    assign: !!appPermissions.assignUserToGA,
  },
});

const buildInitialFormState = (role, initialData = null) => {
  const initialRole = initialData?.role || role || "";
  const fallbackPermissions = buildRolePermissionState(initialRole || ROLES.FIELD_EMPLOYEE);
  return {
    username: initialData?.username || "",
    uniqueId: initialData?.uniqueId || "",
    email: initialData?.email || "",
    mobileNo: initialData?.mobileNo || "",
    password: "",
    role: initialRole,
    appPermissions: {
      ...fallbackPermissions,
      ...(initialData?.permissions?.app || {}),
    },
  };
};

const getBasicStepErrors = (form, mode) => {
  const errors = {};
  if (!form.username.trim()) errors.username = "Name is required";
  if (!form.uniqueId.trim()) errors.uniqueId = "User ID is required";
  if (!form.email.trim()) errors.email = "Email is required";
  if (!form.mobileNo.trim()) errors.mobileNo = "Mobile is required";
  if (mode !== "edit" && !form.password.trim()) errors.password = "Password is required";
  return errors;
};

function CreateUserModal({
  setShowCreateUserModal,
  currentUserRole,
  currentUser,
  mode = "create",
  initialData = null,
  onSuccess,
}) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));
  const [createUser, { isLoading }] = useCreateUserMutation();
  const [editUser, { isLoading: isEditing }] = useEditUserMutation();
  const [currentStep, setCurrentStep] = useState(0);
  const [showStepErrors, setShowStepErrors] = useState(false);
  const [error, setError] = useState("");
  const [advancedExpanded, setAdvancedExpanded] = useState(false);

  const allowedRoles = useMemo(
    () =>
      Array.from(
        new Set([
          ...(ORG_CREATABLE_BY_ROLE[currentUserRole] || []),
          ...(initialData?.role ? [initialData.role] : []),
        ])
      ),
    [currentUserRole, initialData?.role]
  );

  const [form, setForm] = useState(() =>
    buildInitialFormState(allowedRoles[0] || ROLES.FIELD_EMPLOYEE, initialData)
  );

  const initialDataResetKey = [
    mode,
    initialData?._id || "",
    initialData?.role || "",
    initialData?.username || "",
    initialData?.uniqueId || "",
    initialData?.email || "",
    initialData?.mobileNo || "",
  ].join("|");

  useEffect(() => {
    const nextRole = initialData?.role || allowedRoles[0] || ROLES.FIELD_EMPLOYEE;
    setForm(buildInitialFormState(nextRole, initialData));
    setError("");
    setCurrentStep(0);
    setShowStepErrors(false);
    setAdvancedExpanded(false);
  }, [allowedRoles, initialDataResetKey]);

  const steps = [
    { key: "basic", title: "Basic Details", description: "Account information" },
    { key: "preferences", title: "Role Setup", description: "Role selection" },
    { key: "permissions", title: "Permissions", description: "Access and controls" },
  ];

  const basicErrors = useMemo(() => getBasicStepErrors(form, mode), [form, mode]);
  const visibleBasicErrors = showStepErrors && currentStep === 0 ? basicErrors : {};

  const detailFields = [
    { path: "username", label: "Name", required: true },
    {
      path: "uniqueId",
      label: "User ID",
      required: true,
      helperText: "Only a-z, 0-9, dot, underscore, dash.",
    },
    { path: "email", label: "Email", required: true, type: "email" },
    {
      path: "mobileNo",
      label: "Mobile",
      required: true,
      inputProps: { maxLength: 10, inputMode: "numeric" },
      transform: (value) => value.replace(/\D/g, ""),
    },
    {
      path: "password",
      label: "Password",
      type: "password",
      required: mode !== "edit",
      helperText: mode === "edit" ? "Leave blank to keep current password." : "Use a secure password.",
      md: 12,
    },
  ];

  const handleNext = () => {
    if (currentStep === 0 && Object.keys(basicErrors).length) {
      setShowStepErrors(true);
      return;
    }
    setShowStepErrors(false);
    setCurrentStep((prev) => Math.min(prev + 1, steps.length - 1));
  };

  const handleRoleChange = (nextRole) => {
    setForm((prev) => ({
      ...prev,
      role: nextRole,
      appPermissions: buildRolePermissionState(nextRole),
    }));
  };

  const togglePermission = (permissionKey) => {
    if (!hasUserPermission(currentUser, permissionKey)) return;
    setForm((prev) => ({
      ...prev,
      appPermissions: {
        ...prev.appPermissions,
        [permissionKey]: !prev.appPermissions[permissionKey],
      },
    }));
  };

  const handleSubmit = async () => {
    setError("");

    if (Object.keys(basicErrors).length) {
      setCurrentStep(0);
      setShowStepErrors(true);
      return;
    }
    if (!form.role) {
      setCurrentStep(1);
      setError("Role is required");
      return;
    }

    const { devicePermissions, userPermissions } = toLegacyCrudPermissions(form.appPermissions);
    const payload = {
      username: form.username.trim(),
      uniqueId: normalizeUserId(form.uniqueId),
      email: form.email.trim().toLowerCase(),
      mobileNo: String(form.mobileNo || "").trim(),
      ...(form.password?.trim() ? { password: form.password } : {}),
      role: form.role,
      permissions: {
        app: form.appPermissions,
      },
      devicePermissions,
      userPermissions,
    };

    try {
      const result =
        mode === "edit" && initialData?._id
          ? await editUser({ userId: initialData._id, ...payload }).unwrap()
          : await createUser(payload).unwrap();

      onSuccess?.(result);
      setShowCreateUserModal(false);
    } catch (submitError) {
      setError(
        submitError?.data?.msg ||
          (mode === "edit" ? "Failed to update user." : "Failed to create user.")
      );
    }
  };

  const renderRoleStep = () => (
    <Box
      sx={{
        borderRadius: 4,
        border: "1px solid #dbe4f0",
        backgroundColor: "#fff",
        p: { xs: 2, sm: 2.5 },
        boxShadow: "0 20px 40px rgba(15, 23, 42, 0.05)",
      }}
    >
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <TextField
            select
            fullWidth
            label="Role *"
            value={form.role}
            onChange={(event) => handleRoleChange(event.target.value)}
          >
            {allowedRoles.map((role) => (
              <MenuItem key={role} value={role}>
                {ROLE_LABELS[role] || role}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} md={6}>
          <Box
            sx={{
              borderRadius: 3,
              border: "1px solid #dbeafe",
              backgroundColor: "#f8fbff",
              p: 2,
              height: "100%",
            }}
          >
            <Typography sx={{ fontWeight: 800, color: "#1d4ed8" }}>Selected Role</Typography>
            <Typography sx={{ mt: 0.75, fontSize: 16, fontWeight: 800, color: "#0f172a" }}>
              {ROLE_LABELS[form.role] || form.role || "No role selected"}
            </Typography>
            <Typography sx={{ mt: 0.75, fontSize: 13.5, color: "#64748b" }}>
              Role defaults are still generated using the existing permission logic before any manual adjustments.
            </Typography>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );

  const cardItems = (groupTitle) =>
    PERMISSION_GROUPS.find((group) => group.title === groupTitle)?.items.map((item) => ({
      ...item,
      groupKey: groupTitle,
      helperText: hasUserPermission(currentUser, item.key)
        ? "Allowed by your current access."
        : "Locked by parent permission.",
    })) || [];

  const permissionValues = PERMISSION_GROUPS.reduce((acc, group) => {
    acc[group.title] = {};
    group.items.forEach((item) => {
      acc[group.title][item.key] = Boolean(form.appPermissions?.[item.key]);
    });
    return acc;
  }, {});

  const parentAllowed = {
    "View Permissions": Object.fromEntries(
      (PERMISSION_GROUPS.find((group) => group.title === "View Permissions")?.items || []).map((item) => [
        item.key,
        hasUserPermission(currentUser, item.key),
      ])
    ),
    "User Management": Object.fromEntries(
      (PERMISSION_GROUPS.find((group) => group.title === "User Management")?.items || []).map((item) => [
        item.key,
        hasUserPermission(currentUser, item.key),
      ])
    ),
    "Device Management": Object.fromEntries(
      (PERMISSION_GROUPS.find((group) => group.title === "Device Management")?.items || []).map((item) => [
        item.key,
        hasUserPermission(currentUser, item.key),
      ])
    ),
    "Assets And Credits": Object.fromEntries(
      (PERMISSION_GROUPS.find((group) => group.title === "Assets And Credits")?.items || []).map((item) => [
        item.key,
        hasUserPermission(currentUser, item.key),
      ])
    ),
    "Tasks And Other": Object.fromEntries(
      (PERMISSION_GROUPS.find((group) => group.title === "Tasks And Other")?.items || []).map((item) => [
        item.key,
        hasUserPermission(currentUser, item.key),
      ])
    ),
  };

  return (
    <WizardModalLayout
      open
      onClose={() => setShowCreateUserModal(false)}
      icon={AccountTreeOutlinedIcon}
      title={mode === "edit" ? "Edit Organization User" : "Create Organization User"}
      subtitle={
        mode === "edit"
          ? "Update organization user access in 3 guided steps."
          : "Create an organization user in 3 simple steps."
      }
      steps={steps}
      currentStep={currentStep}
      onStepChange={setCurrentStep}
      error={error}
      helpTitle="Need Help?"
      helpText="Choose the role first, then fine-tune permissions. Existing payload structure and legacy CRUD permission mapping stay unchanged."
      fullScreen={fullScreen}
      maxWidth="xl"
      footer={
        <StickyModalFooter
          currentStep={currentStep}
          steps={steps}
          loading={isLoading || isEditing}
          onBack={() => setCurrentStep((prev) => Math.max(prev - 1, 0))}
          onCancel={() => setShowCreateUserModal(false)}
          onNext={handleNext}
          onSubmit={handleSubmit}
          submitLabel={mode === "edit" ? "Update User" : "Create User"}
        />
      }
    >
      {currentStep === 0 ? (
        <BasicDetailsStep
          title="Account Information"
          description="Enter the basic account details for this organization user."
          form={form}
          onChange={(path, value) =>
            setForm((prev) => ({
              ...prev,
              [path]: path === "uniqueId" ? normalizeUserId(value) : value,
            }))
          }
          fields={detailFields}
          errors={visibleBasicErrors}
          tip="This form still sends the same create/edit payload and legacy device/user permission objects."
        />
      ) : null}

      {currentStep === 1 ? renderRoleStep() : null}

      {currentStep === 2 ? (
        <Box sx={{ display: "grid", gap: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <PermissionToggleCard
                title="Device Access"
                description="Device management rights for the selected role."
                items={cardItems("Device Management").map((item) => ({ ...item, groupKey: "Device Management" }))}
                values={permissionValues}
                parentAllowed={parentAllowed}
                onToggle={(_, itemKey) => togglePermission(itemKey)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <PermissionToggleCard
                title="User Access"
                description="User creation, edits, reset password, and dealer assignment."
                items={cardItems("User Management").map((item) => ({ ...item, groupKey: "User Management" }))}
                values={permissionValues}
                parentAllowed={parentAllowed}
                onToggle={(_, itemKey) => togglePermission(itemKey)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <PermissionToggleCard
                title="Data & Alerts"
                description="Read access across dashboard, events, devices, and protocols."
                items={cardItems("View Permissions").map((item) => ({ ...item, groupKey: "View Permissions" }))}
                values={permissionValues}
                parentAllowed={parentAllowed}
                onToggle={(_, itemKey) => togglePermission(itemKey)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <PermissionToggleCard
                title="Other Access"
                description="Asset and credits access for this role."
                items={cardItems("Assets And Credits").map((item) => ({ ...item, groupKey: "Assets And Credits" }))}
                values={permissionValues}
                parentAllowed={parentAllowed}
                onToggle={(_, itemKey) => togglePermission(itemKey)}
              />
            </Grid>
          </Grid>

          <Accordion
            expanded={advancedExpanded}
            onChange={(_, expanded) => setAdvancedExpanded(expanded)}
            sx={{
              borderRadius: 4,
              border: "1px solid #eadcff",
              boxShadow: "none",
              backgroundColor: alpha(theme.palette.secondary.light, 0.12),
              "&::before": { display: "none" },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box>
                <Typography sx={{ fontWeight: 800 }}>Advanced Settings</Typography>
                <Typography sx={{ fontSize: 13, color: "#64748b" }}>
                  Tasks, messaging, and other advanced access.
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <PermissionToggleCard
                title="Advanced Controls"
                description="All toggles remain limited by the current user's own access."
                items={cardItems("Tasks And Other").map((item) => ({ ...item, groupKey: "Tasks And Other" }))}
                values={permissionValues}
                parentAllowed={parentAllowed}
                onToggle={(_, itemKey) => togglePermission(itemKey)}
              />
            </AccordionDetails>
          </Accordion>
        </Box>
      ) : null}
    </WizardModalLayout>
  );
}

export default CreateUserModal;
